import { morph } from '@theme/morph';
import { isMobileBreakpoint } from '@theme/utilities';

class SuggestedAddons extends HTMLElement {
  #cachedProductGrids = new Map();
  #quickViewAbortController = null;
  #quickViewRequestId = 0;
  #resizeObserver = null;

  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('click', this.#handleClick);

    const scroller = this.querySelector('[data-addons-scroller]');
    scroller?.addEventListener('scroll', this.#updateProgress, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#updateProgress);
    if (scroller) this.#resizeObserver.observe(scroller);
    requestAnimationFrame(this.#updateProgress);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('click', this.#handleClick);
    this.querySelector('[data-addons-scroller]')?.removeEventListener('scroll', this.#updateProgress);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  #handleChange = (event) => {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement) || checkbox.name !== 'addon') return;

    checkbox.closest('.suggested-addons__card')?.toggleAttribute('data-selected', checkbox.checked);
  };

  #updateProgress = () => {
    const scroller = this.querySelector('[data-addons-scroller]');
    const progress = this.querySelector('[data-addons-progress]');
    const indicator = this.querySelector('[data-addons-progress-indicator]');
    if (!(scroller instanceof HTMLElement) || !(progress instanceof HTMLElement) || !(indicator instanceof HTMLElement)) return;

    const scrollableWidth = scroller.scrollWidth - scroller.clientWidth;
    const hasOverflow = scrollableWidth > 1;
    progress.toggleAttribute('data-overflow', hasOverflow);
    if (!hasOverflow) return;

    const trackWidth = progress.clientWidth;
    const indicatorWidth = Math.max(trackWidth * (scroller.clientWidth / scroller.scrollWidth), 32);
    const maxOffset = trackWidth - indicatorWidth;
    const offset = maxOffset * (scroller.scrollLeft / scrollableWidth);

    indicator.style.width = `${indicatorWidth}px`;
    indicator.style.transform = `translateX(${offset}px)`;
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.suggested-addons__title, .suggested-addons__checkbox-label')) return;

    const card = event.target.closest('.suggested-addons__card');
    if (!(card instanceof HTMLElement)) return;

    const trigger = card.querySelector('[data-addon-quick-view]');
    if (!(trigger instanceof HTMLButtonElement)) return;

    this.#openQuickView(card, trigger);
  };

  /** @param {HTMLElement} card @param {HTMLButtonElement} trigger */
  async #openQuickView(card, trigger) {
    const productUrl = card.dataset.addonProductUrl;
    if (!productUrl || trigger.disabled) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) {
      window.location.assign(productUrl);
      return;
    }

    const requestId = ++this.#quickViewRequestId;
    this.#quickViewAbortController?.abort();
    this.#quickViewAbortController = new AbortController();

    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');
    this.#showQuickViewLoading(modalContent);
    this.#showQuickViewDialog();

    try {
      let productGrid = this.#cachedProductGrids.get(productUrl);

      if (!productGrid) {
        const response = await fetch(productUrl, { signal: this.#quickViewAbortController.signal });
        if (!response.ok) throw new Error(`Unable to load product: ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const productGridContent = html.querySelector('[data-product-grid-content]');
        if (!productGridContent) throw new Error('Quick-view content is unavailable for this product');

        productGrid = /** @type {Element} */ (productGridContent.cloneNode(true));
        this.#cachedProductGrids.set(productUrl, productGrid);
      }

      if (requestId !== this.#quickViewRequestId) return;
      await this.#updateQuickView(/** @type {Element} */ (productGrid.cloneNode(true)), productUrl, card.dataset.addonProductTitle || '');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('[suggested-addons] Quick view failed:', error);
      window.location.assign(productUrl);
    } finally {
      trigger.disabled = false;
      trigger.removeAttribute('aria-busy');

      if (requestId === this.#quickViewRequestId) {
        this.#quickViewAbortController = null;
      }
    }
  }

  /** @param {HTMLElement} modalContent */
  #showQuickViewLoading(modalContent) {
    const loader = document.createElement('div');
    loader.className = 'suggested-addons__quick-view-loading';
    loader.setAttribute('role', 'status');
    loader.setAttribute('aria-live', 'polite');
    loader.innerHTML = `
      <div class="suggested-addons__quick-view-skeleton-media" aria-hidden="true"></div>
      <div class="suggested-addons__quick-view-skeleton-details">
        <p class="suggested-addons__quick-view-skeleton-kicker">Loading your pick</p>
        <span class="suggested-addons__quick-view-skeleton-line suggested-addons__quick-view-skeleton-line--title" aria-hidden="true"></span>
        <span class="suggested-addons__quick-view-skeleton-line suggested-addons__quick-view-skeleton-line--short" aria-hidden="true"></span>
        <div class="suggested-addons__quick-view-skeleton-options" aria-hidden="true">
          <span></span><span></span><span></span>
        </div>
        <span class="suggested-addons__quick-view-skeleton-button" aria-hidden="true"></span>
        <p class="suggested-addons__quick-view-skeleton-message">Getting the details ready…</p>
      </div>
    `;

    modalContent.setAttribute('aria-busy', 'true');
    modalContent.replaceChildren(loader);
  }

  #showQuickViewDialog() {
    const dialog = document.getElementById('quick-add-dialog');
    if (dialog && typeof dialog.showDialog === 'function') {
      dialog.showDialog();
      return;
    }

    customElements.whenDefined('quick-add-dialog').then(() => {
      const quickViewDialog = document.getElementById('quick-add-dialog');
      if (quickViewDialog && typeof quickViewDialog.showDialog === 'function') quickViewDialog.showDialog();
    });
  }

  /** @param {Element} productGrid @param {string} productUrl @param {string} productTitle */
  async #updateQuickView(productGrid, productUrl, productTitle) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) throw new Error('Quick-view modal is unavailable');

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productHeader = document.createElement('div');
      const title = document.createElement('a');

      title.textContent = productTitle;
      title.href = productUrl;
      productHeader.classList.add('product-header');
      productHeader.append(title);
      if (productPrice) productHeader.append(productPrice);
      productGrid.append(productHeader);
      if (variantPicker) productGrid.append(variantPicker);
      if (productFormComponent) productGrid.append(productFormComponent);
      productDetails?.remove();
    }

    modalContent.setAttribute('view-event-payload', productGrid.getAttribute('view-event-payload') || '');
    modalContent.removeAttribute('aria-busy');
    morph(modalContent, productGrid);
  }
}

if (!customElements.get('suggested-addons')) {
  customElements.define('suggested-addons', SuggestedAddons);
}
